(function (angular) {
    'use strict';
    var app = angular.module('enervee');
    /**
     * EnerveeCheckoutService is responsible for handling products from enervee checkout
     */
    app.factory('EnerveeCheckoutService', [
        '$q',
        '$log',
        '$location',
        '$window',
        'AccountAuthService',
        'AnalyticsService',
        'CartService',
        'cloudinary',
        'IndexService',
        'UtilService',
        'ProductService',
        'CmsContentService',
        'UtilitySelectorCookieService',
        'ProductApiConsumerService',
        'SwiftypeService',
        'WITH_CREDENTIALS',
        function (
            $q,
            $log,
            $location,
            $window,
            AccountAuthService,
            AnalyticsService,
            CartService,
            cloudinary,
            IndexService,
            UtilService,
            ProductService,
            CmsContentService,
            UtilitySelectorCookieService,
            ProductApiConsumerService,
            SwiftypeService,
            WITH_CREDENTIALS
        ) {
            var _pub = {};
            var CAPI_PROXY_URL = 'checkout-api/api/';
            var checkoutEnabled = UtilService.isFeatureEnabled('checkout_enabled');
            var _cartIsSetup = $q.defer();
            var _cartCreation;
            var _cartId;

            var error500Message = {};
            CmsContentService.retrieveCMSValue(error500Message, 'title', 'base:modals:checkout_error_500_title');
            CmsContentService.retrieveCMSValue(error500Message, 'body', 'base:modals:checkout_error_500_body');

            if (checkoutEnabled) {
                initCart();
                AccountAuthService.onEveryAuthChange('refreshCart', reloadCart);
            }

            /**
             * Queries enervee checkout for products in the specified category.
             * @param {Number} categoryId
             * @param {Array} productIds Array of product id.
             * @returns {*}
             */
            _pub.fetchCheckoutProducts = function (categoryId, productIds) {
                var utilityId = UtilitySelectorCookieService.getUtilityId();
                return ProductApiConsumerService.get(CAPI_PROXY_URL + 'products/',
                    {
                        params: {
                            utility_id: utilityId,
                            category_id: categoryId,
                            products: productIds,
                        }
                    }
                ).then(function (results) {
                    var products = results.data.products || [];
                    products.forEach(function(product) {
                        product.total_additional_discounts = parseFloat(product.total_additional_discounts || "0.00");
                    });
                    return products;
                }).catch(function(error) {
                    $log.warn(error);
                });
            };

            /**
             * Applies data from checkout products to an array of marketplace products
             *
             * @param {Array<Object>} products
             * @param {Array<Object>} checkoutProducts
             */
            _pub.applyCheckoutDataToProducts = function (products, checkoutProducts) {
                // TODO: probably this needs to be updated to accept 1 or more products
                var checkoutProductsById = {};
                checkoutProducts = checkoutProducts || [];
                checkoutProducts.forEach(function (checkoutProduct) {
                    checkoutProduct.id = checkoutProduct.product_id;
                    checkoutProductsById[checkoutProduct.product_id] = checkoutProduct;
                });
                products.forEach(function (product) {
                    if (product.lowest_priced_child) {
                        product.lowest_priced_child = SwiftypeService.getLowestPricedChild(product, checkoutProductsById);
                    }
                    if (checkoutProductsById.hasOwnProperty(product.id)) {
                        applyCheckoutDataToProduct(product, checkoutProductsById[product.id], product);
                    } else if( product.lowest_priced_child !== null
                        && checkoutProductsById.hasOwnProperty(product.lowest_priced_child.id)) {
                        var child = product.lowest_priced_child;
                        //use the child offered for checkout
                        product = angular.extend(product, child);
                        product.link = child.product_short_link;
                        product.thumbnail_image = child.image;
                        applyCheckoutDataToProduct(product, checkoutProductsById[product.lowest_priced_child.id], child);
                    }
                });
            };

            function applyCheckoutDataToProduct (product, checkoutData, child) {
                product.checkoutEnabled = true;
                product.checkoutInfo = checkoutData;
                product.checkoutInStock = checkoutData.in_stock;
                product.checkoutMSRP = checkoutData.price;
                if (child.hasOwnProperty('rebate')) {
                    product.checkoutHasRebate = child.rebate !== null;
                    if (product.checkoutHasRebate) {
                        product.checkoutInfo.rebate = child.rebate;
                    }
                }
            }

            /**
             * Returns a deferred promise to wait for the cart to be initialized.
             *
             * @return {Promise} resolves when the cart is setup
             */
            _pub.waitForCart = function () {
                return _cartIsSetup.promise;
            };

            /**
             * Gets the existing cart.
             * Waits for the service to be initialized, then returns the user's cart ID
             * by getting the current one or creating a new cart.
             *
             * @return {Promise} resolves with the user's cart ID
             */
            _pub.getCartId = function () {
                if (typeof _cartId !== "undefined") {
                    return $q.resolve(_cartId);
                }
                return createCart().then(function(cartDetails) {
                    _cartId = cartDetails.id;
                    return cartDetails.id;
                });
            };

            /**
             * Sends a request to copy a cart.
             * Waits for the service to be initialized, then returns the copied cart ID.
             *
             * @return {Promise} resolves with the copied cart ID
             */
            _pub.getCartIdCopy = function() {
                if (typeof _cartId === "undefined") {
                    return $q.reject("Can't copy cart: no cart id")
                } else {
                    const url = CAPI_PROXY_URL + 'carts/copy/';
                    return ProductApiConsumerService.get(url, WITH_CREDENTIALS).then(function (response) {
                        return $q.resolve(response.data.cart_id);
                    });
                }
            };

            /**
             * Get the orders for a specific user.
             *
             * @return {Promise} Resolves with a list of the user's orders
             */
            _pub.getOrders = function() {
                const config = { ...WITH_CREDENTIALS, params: { include_lines: true }};
                return ProductApiConsumerService.get(CAPI_PROXY_URL + 'orders/', config ).then(function (response) {
                    // Add images to each product
                    var orders = response.data;
                    var imagePromises = [];
                    orders.forEach(function (order) {
                        order.total_tax = parseFloat(order.cart_charge.tax) + parseFloat(order.shipping_charge.tax);
                        order.lines.forEach(function (line) {
                            var promise = getProductImage(line.id).then(function (productImage) {
                                line.image = productImage;
                            });
                            if (line.rebate || line.additional_discounts) {
                                const rebate = line.rebate ? (line.rebate.amount * line.rebate.quantity) : 0;
                                line.total_additional_discount = line.additional_discounts ? (line.additional_discounts.amount * line.additional_discounts.quantity) : 0;
                                line.price_after_discounts = line.price - (rebate + line.total_additional_discount);
                            } else {
                                line.price_after_discounts = line.price;
                            }
                            imagePromises.push(promise);
                        });
                    });
                    return $q.all(imagePromises).then(function () {
                        return orders;
                    });
                }, function() {
                    return [];
                });
            };

            /**
             * Adds a product to the CAPI Cart, then updates the marketplace cart.
             *
             * @param {Number} productId
             * @param {Number} quantity
             * @return {Promise} resolves upon success
             */
            _pub.addProductToCart = function(productId, quantity) {
                var payload = { id: productId, quantity: quantity };
                return _pub.getCartId().then(function () {
                    return ProductApiConsumerService.post(CAPI_PROXY_URL + 'carts/add-product/', payload, WITH_CREDENTIALS).then(function (response) {
                        setCartDetails(response.data);
                        var returnedProducts = response.data.products;
                        return applyCartProductsChanges([payload], returnedProducts).then(function () {
                            // whenever a new product is added to the cart, we want to go back to the total page.
                            var resetCartView = true;
                            CartService.openCart(resetCartView);
                            return returnedProducts;
                        });
                    }).catch(function(response) {
                        if (response.status == 409) {
                            // 409 indicates a fulfillment partner conflict, so present the replace cart modal
                            let partners = response.data.detail.split("::");
                            const replaceCartInfo = {
                                'productBeingAdded': payload,
                                'partnerBeingReplaced': partners[0],
                                'partnerBeingAdded': partners[1]
                            };
                            IndexService.setReplaceCartInfo(replaceCartInfo);
                            IndexService.openModal('#replace-cart');
                            AnalyticsService.reportEvent('Replace Cart Modal Viewed', {});
                        };
                    });
                });
            };

            /**
             * Updates the quantities and/or rebate quantities of products in the CAPI Cart, then
             * updates the marketplace cart.
             *
             * @param {Array<Object>} products
             * @param {Number} products[].id
             * @param {Number} [products[].quantity]
             * @param {Number} [products[].rebate_quantity]
             * @return {Promise} resolves upon success
             */
            _pub.updateProductsInCart = function(products) {
                return ProductApiConsumerService.put(CAPI_PROXY_URL + 'carts/', { products }, WITH_CREDENTIALS).then(function (response) {
                    setCartDetails(response.data);
                    var returnedProducts = response.data.products;
                    return applyCartProductsChanges(products, returnedProducts).then(function () {
                        return returnedProducts;
                    });
                });
            };

            /**
             * Create a new cart and get its initial details
             *
             * @return {Promise} resolves with cart details
             */
            function createCart(force) {
                if (force || typeof _cartCreation === "undefined") {
                    _cartCreation = ProductApiConsumerService.post(CAPI_PROXY_URL + 'carts/', $location.search(), WITH_CREDENTIALS).then(function (response) {
                        setCartDetails(response.data);
                        return response.data;
                    });
                }
                return _cartCreation;
            }

            /**
             * Get the details of an existing cart. The promise resolves if the customer's cart details
             * were found, and rejects if there were no details retrieved (either because the user did
             * not have a cart, or their previous cart had already been submitted).
             *
             * @return {Promise} Resolves with cart details or rejects with the failed response
             */
            function getCartDetails() {
                return ProductApiConsumerService.get(CAPI_PROXY_URL + 'carts/', WITH_CREDENTIALS).then(function (response) {
                    // If we get a 204, the user does not have a cart yet, so we'll just abort
                    if (response.status === 204) return $q.reject(response);
                    const cartDetails = response.data;
                    setCartDetails(cartDetails);
                    return cartDetails;
                });
            }

            /**
             * Either creates a new application for the cart or updates the existing one with the
             * cart's products
             * @param {Object} context
             *
             * @return {Promise} resolves with application_id and url
             */
            _pub.createOrUpdateApplication = function () {
                const data = { context: { source: 'checkout' }};
                return ProductApiConsumerService.post(CAPI_PROXY_URL + 'carts/application/create-or-update/', data, WITH_CREDENTIALS).then(function (response) {
                    return response.data;
                });
            };

            /**
             * Opens an alert modal with a generic "please try again later message"
             */
            _pub.openErrorModal = function () {
                UtilService.openAlertModal({
                    title: error500Message.title,
                    body: error500Message.body,
                });
            };

            /**
             * Attempts to get the current cart details from the backend. If the response isn't ok,
             * then a cart doesn't exist yet, and we can say the cart is set up.
             *
             * @return {Promise} resolves when the cart has been created.
             */
            function initCart() {
                getCartDetails().then(function (cartDetails) {
                    // Setup the cart products
                    var imagePromises = [];
                    cartDetails.products.forEach(function (product) {
                        var promise = getProductImage(product.id).then(function (productImage) {
                            product.image = productImage;
                        });
                        imagePromises.push(promise);
                    });
                    if (imagePromises.length === 0) {
                        return _cartIsSetup.resolve();
                    }
                    $q.all(imagePromises).finally(function () {
                        cartDetails.products.forEach(function (product) {
                            CartService.addItem(product);
                        });
                        _cartIsSetup.resolve();
                    });
                }, function () {
                    // If an error occurs getting the cart, assume that a cart doesn't exist for
                    // this session yet.
                    _cartIsSetup.resolve();
                });
                return _cartIsSetup.promise;
            }

            /**
             * Helper function to set the current cart ID.
             */
            function setCartDetails(cartDetails) {
                if (!cartDetails) return;
                _cartId = cartDetails.id;
                CartService.cartDetails.cartId = _cartId;
                CartService.cartDetails.fulfillmentPartnerName = cartDetails.fulfillment_partner_name;
                CartService.cartDetails.fulfillmentPartnerId = cartDetails.fulfillment_partner_id;
                CartService.cartDetails.redirectUrl = cartDetails.fulfillment_partner_checkout_redirect_url;
            }

            /**
             * Helper function to retrieve product images.
             * TODO: Should be removed once we serve product images from CAPI
             *
             * @param {Number} productId
             * @return {Promise} resolves with product image data
             */
            function getProductImage(productId) {
                return ProductService.getProductImagesForCloudinary(productId).then(function (productImages) {
                    if (productImages.length === 0) {
                        return '';
                    }
                    var url = cloudinary.url(productImages[0]);
                    return url;
                }, function (err) {
                    $log.warn("Cannot find product image for product_id: " + productId);
                    $log.error(err);
                    return "";
                });
            }

            /**
             * Helper function to reload the cart. It clears the cart in CartService,
             * resets the cart ID, and then initializes the cart.
             */
            function reloadCart() {
                _cartIsSetup = $q.defer();
                _pub.clearCart();
                initCart();
            }

             /**
             * Clears the cart and removes the cart ID from session storage.
             */
            _pub.clearCart = function () {
                $window.sessionStorage.removeItem('cartId');
                _cartId = undefined;
                _cartCreation = undefined;
                CartService.clearAllItems();
            }

            /**
             * Helper function to get an object in an array by its `id` property
             */
            function getItemById(items, id) {
                for (var i = 0; i < items.length; i++) {
                    if (items[i].id === id) {
                        return items[i];
                    }
                }
            }

            /**
             * Applies changes to the cart, given the products sent to the server and the products
             * from the CAPI response. Assumes that product ids sent to the server but not sent
             * back have been removed from the cart and their quantity should be set to 0.
             *
             * @param {Array<Object>} products the products passed to the server
             * @param {Array<Object>} returnedProducts the products _returned_ by the server
             * @return {Promise} resolves when all products have been added or updated in the cart
             */
            function applyCartProductsChanges(products, returnedProducts) {
                // Copy the returnedProducts array, cause we're going to modify it
                returnedProducts = returnedProducts.slice(0);
                // Go over the returnedProducts to make sure the removed products are also updated
                products.forEach(function (product) {
                    var productWasRemoved = !getItemById(returnedProducts, parseInt(product.id));
                    if (productWasRemoved) {
                        returnedProducts.push({
                            id: product.id,
                            quantity: 0,
                        });
                    }
                });

                var productUpdatePromises = returnedProducts.map(function (returnedProduct) {
                    return addOrUpdateItem(returnedProduct, 'set');
                });
                return $q.all(productUpdatePromises);
            }

            /**
             * Adds or updates a product in the cart (after requesting the image for the product)
             *
             * @param {Object} product a product returned from the backend, or just an object with
             * an id and a quantity (for products that get removed from the cart)
             * @param {String} quantityMode ('increment' || 'set')
             */
            function addOrUpdateItem(product, quantityMode) {
                /**
                  * TODO: eventually we should serve images from CAPI,
                  * but we'll fetch them with the product service for now.
                  */
                return getProductImage(product.id).then(function (productImage) {
                    product.image = productImage;
                    CartService.addItem(product, {quantityMode: quantityMode});
                });
            }

            /**
             * Fetches the category ID's for categories that have products
             * @return {Promise}
             */
            _pub.fetchCheckoutCategories = function () {
                var utilityId = UtilitySelectorCookieService.getUtilityId();
                return ProductApiConsumerService.get(
                    CAPI_PROXY_URL + 'categories/',
                    {params: {utility_id: utilityId}}
                ).then(function (response) {
                    return response.data;
                });
            };

            /**
             * If Checkout is enabled, fetches the checkout categories and applies the data to
             * the appropriate categories in the array passed. Returned promise resolves immediately
             * if Checkout is not enabled.
             * _NOTE_: modifies the `categories` array in place.
             *
             * @param {Array<Object>} categories
             * @param {Number | String} categories[].id
             * @return {Promise}
             */
            _pub.applyCheckoutEnabledToCategories = function (categories) {
                if (checkoutEnabled) {
                    return _pub.fetchCheckoutCategories().then(function (checkoutCategories) {
                        categories.forEach(function (category) {
                            category.is_checkout_enabled = (checkoutCategories.indexOf(category.id) !== -1);
                        });
                    });
                }
                return $q.resolve();
            };

            /**
             * Fetches best rebate for each product.
             *
             * @returns {Promise} a promise that resolves with the best rebates found for the
             * products in the cart.
             * Example response:
             * [
             *   {
             *     "product_id": 1,
             *     "category_id": 1,
             *     "product_quantity": 1,
             *     "rebate_id": 1,
             *     "rebate_amount": 100,
             *     "rebate_quantity": 1,
             *     "reason": null,
             *   },
             *   {
             *     "product_id": 2,
             *     "category_id": 1,
             *     "product_quantity": 2,
             *     "rebate_id": null,
             *     "rebate_amount": null,
             *     "rebate_quantity": 0,
             *     "reason": "Limit of 2 rebates per year",
             *   }
             * ]
             */
            _pub.getBestRebates = function () {
                return ProductApiConsumerService.post(CAPI_PROXY_URL + 'carts/best-rebates/', {}, WITH_CREDENTIALS).then(function (response) {
                    return _pub.transformRebateReasonsData(response.data);
                });
            };

            /**
             * Transform rebate reason for each rebated product
             * @param {Array} rebated products
             * @return {Array} updated rebated products
           */
            _pub.transformRebateReasonsData = function (rebatedProducts) {
                rebatedProducts.forEach(function (rebatedProduct) {
                    if (rebatedProduct.reason) {
                        // v2 will return an array
                        if (Array.isArray(rebatedProduct.reason)) {
                            // filter out reasons for combo rebates
                            rebatedProduct.reason = rebatedProduct.reason.filter(function(item) {
                                const businessRuleCodes = item.rebate.business_rule_codes;
                                return businessRuleCodes && businessRuleCodes.length === 1;
                            });
                        // if v2 is rolled back, v1 will return a string
                        } else {
                            // transform the data structure to work with lastest version of webapp
                            rebatedProduct.reason = [
                                {
                                    "rebate": {"title": '', "amount": ''},
                                    "reasons": [{"reason": rebatedProduct.reason}]
                                }
                            ]
                        }
                    } else {
                        // For backwards compatibility
                        rebatedProduct.reason = null;
                    }
                });

                return rebatedProducts;
            };

            /**
             * Retrieve the customer details for the current user's cart.
             * @return {Object} An object containing the customer's information.
             * 
             * Example response: 
             *   {
             *       "phone_number": 1234567890,
             *       "address": [
             *           {
             *               "address_line_1": "5535 Westlawn Avenue",
             *               "address_line_2": "APT 211",
             *               "city": "Los Angeles",
             *               "state": "CA",
             *               "zip_code": "90066",
             *               "country": "US"
             *           }
             *       ],
             *       "last_name": "Smith",
             *       "first_name": "John",
             *       "email": "user@email.com"
             *   }
             */
            _pub.getCustomerDetails = function() {
                return ProductApiConsumerService.get(CAPI_PROXY_URL + 'carts/customer-details/', WITH_CREDENTIALS)
                    .then(response => response.data);
            }

            /**
             * Check URL search string for products to add to cart
             * add them to cart with corresponding quantity.
             */
            _pub.initializeCartFromURL = function () {
                //check first if checkout enabled
                if (checkoutEnabled) {
                    var addProductPromises = [];
                    var params = $location.search();
                    if ('add-to-cart' in params) {
                        var productIds = params['add-to-cart'].split(',');
                        var quantities = [];
                        if (params.quantity) {
                            quantities = params.quantity.split(',');
                        }
                        for (var index in productIds) {
                            var id = productIds[index];
                            var quantity = quantities[index] || 1;
                            addProductPromises.push(_pub.addProductToCart(id, quantity));
                        }
                    }

                    return $q.all(addProductPromises);
                }
                return $q.resolve([]);
            };

            return _pub;
        }
    ]);
})(angular);
